import { ErrorMessage, Field } from 'formik';
import { Col, Form, FormControl, InputGroup, Row, Spinner, Stack } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { BsCurrencyDollar } from 'react-icons/bs';
import Editor from '../../../../../../components/editor/Editor';
import { FormLabel, FormStepHeader } from '../../../../../../components/forms';
import { NumberInput } from '../../../../../../components/inputs/NumberInput';
import { useToast } from '../../../../../../context/ToastContext';
import { useGetUsersQuery, User } from '../../../../../../services/endpoints/people/user';
import { useGetAllInstructorCourseRequestsQuery } from '../../../../../../services/endpoints/schedule/instructorCourseRequest';
import { InstructorCourseRequest } from '../../../../../../types/course.type';
import { formatDate } from '../../../../../../utils/dateUtils';
import { getErrorMessage } from '../../../../../../utils/utils';
import { StepProps } from '../AddEditSession';
import { formatInTimeZone } from 'date-fns-tz';

const FALLBACK_TZ = 'America/Denver';

export const InstructorDetailsForm = ({
  touched,
  errors,
  values,
  setFieldValue,
  readOnly
}: StepProps) => {
  const courseId = values?.id;
  // Locked starting the day after the first class (editable on first-class day).
  const firstClassDate = [values?.starts_on, ...(values?.classes?.map((c) => c.date) ?? [])]
    .map((d) => d?.slice(0, 10))
    .filter((d): d is string => !!d && /^\d{4}-\d{2}-\d{2}$/.test(d))
    .sort()[0];
  const today = formatInTimeZone(new Date(), values?.venue_timezone || FALLBACK_TZ, 'yyyy-MM-dd');
  const instructorsLocked = !!firstClassDate && firstClassDate < today;
  const showInstructorTypeahead = !readOnly && !instructorsLocked;

  const { data, error, isLoading } = useGetUsersQuery(
    {
      scope: 'colleagues',
      data: { entries: 1000, orderBy: { field: 'first_name', order: 'asc' } }
    },
    { skip: !showInstructorTypeahead }
  );
  const { data: requestsData } = useGetAllInstructorCourseRequestsQuery(
    {
      data: {
        filterBy: [{ field: 'course_id', operator: 'equals', value: courseId! }],
        orderBy: { field: 'created_at', order: 'desc' },
        entries: 100
      }
    },
    { skip: !courseId }
  );
  const sessionRequests = (requestsData?.results ?? []) as InstructorCourseRequest[];
  const toast = useToast();

  if (showInstructorTypeahead && isLoading) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  if (showInstructorTypeahead && (error || !data || !data.results)) {
    toast.addToast(getErrorMessage(error) ?? 'Error loading themes', 'error');
  }

  const instructorNames =
    values?.instructor_ids?.map((instructor) => instructor.label).filter(Boolean) ?? [];

  return (
    <Row>
      <Col xs={12}>
        <FormStepHeader title="Instructor Details" showRequiredLegend={!readOnly} />
      </Col>
      <Col
        xs={12}
        lg={6}
      >
        <Stack gap={3}>
          <Form.Group controlId="min_instructors">
            <FormLabel required>Main Instructors Required</FormLabel>
            <Field
              name="min_instructors"
              min="1"
              as={NumberInput}
              style={{ width: 66 }}
              isInvalid={touched.min_instructors && !!errors.min_instructors}
              disabled={readOnly}
              aria-required
            />
            <ErrorMessage name="min_instructors">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group controlId="instructor_ids">
            <FormLabel>Main Instructors</FormLabel>
            {showInstructorTypeahead ? (
              <Typeahead
                id="instructor_ids"
                multiple
                flip
                positionFixed
                options={
                  data?.results?.map((instructor: User) => ({
                    id: instructor.id,
                    label: `${instructor.first_name} ${instructor.last_name}`
                  })) ?? []
                }
                selected={values?.instructor_ids}
                onChange={(selected) => setFieldValue!('instructor_ids', selected)}
                isInvalid={touched.instructor_ids && !!errors.instructor_ids}
              />
            ) : (
              <div className="form-control-plaintext">
                {instructorNames.length > 0 ? instructorNames.join(', ') : 'None'}
              </div>
            )}
            <ErrorMessage name="instructor_ids">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
            {sessionRequests.length > 0 && (
              <div className="mt-2 text-muted small">
                {sessionRequests.map((request) => (
                  <div key={request.id}>
                    {request.instructor?.full_name ?? 'An instructor'} requested this session at{' '}
                    {formatDate(new Date(request.created_at), 'MM/dd/yyyy h:mm zzz')}
                  </div>
                ))}
              </div>
            )}
          </Form.Group>

          <Form.Group controlId="pay_type">
            <FormLabel
              className={touched.pay_type && errors.pay_type ? 'text-danger' : undefined}
              required
            >
              Instructor Pay Type
            </FormLabel>
            <Field name="pay_type">
              {({ field }: { field: any }) => (
                <>
                  <Form.Check
                    type="radio"
                    label="Flat Rate"
                    name="pay_type"
                    id="flat_rate"
                    value="flat_rate"
                    onChange={() => setFieldValue!('pay_type', 'flat_rate')}
                    checked={field.value === 'flat_rate'}
                    disabled={readOnly}
                    isInvalid={touched.pay_type && !!errors.pay_type}
                  />
                  <Form.Check
                    type="radio"
                    label="Per hour"
                    name="pay_type"
                    id="per_hour"
                    value="per_hour"
                    onChange={() => {
                      setFieldValue!('pay_type', 'per_hour');
                      setFieldValue!('flat_rate', undefined);
                    }}
                    checked={field.value === 'per_hour'}
                    disabled={readOnly}
                    isInvalid={touched.pay_type && !!errors.pay_type}
                  />
                </>
              )}
            </Field>
            <ErrorMessage name="pay_type">
              {(msg) => (
                <FormControl.Feedback
                  type="invalid"
                  className="d-block"
                >
                  {msg}
                </FormControl.Feedback>
              )}
            </ErrorMessage>
          </Form.Group>

          {values?.pay_type === 'flat_rate' && (
            <Form.Group controlId="flat_rate">
              <FormLabel required>Instructor Flat Daily Rate</FormLabel>
              <InputGroup>
                <InputGroup.Text>
                  <BsCurrencyDollar color="#5E6469" />
                </InputGroup.Text>
                <Field
                  name="flat_rate"
                  as={NumberInput}
                  isInvalid={touched.flat_rate && !!errors.flat_rate}
                  disabled={readOnly}
                  aria-required
                ></Field>
              </InputGroup>
              <ErrorMessage
                name="flat_rate"
                component="span"
                className="text-danger fs-small"
              />
            </Form.Group>
          )}
        </Stack>
      </Col>
      <Col
        xs={12}
        lg={12}
      >
        <Stack gap={3}>
          <Form.Group controlId="instructor_notes">
            <FormLabel>Instructor Notes</FormLabel>

            <Field
              name="instructor_notes"
              component={Editor}
              className="form-control ck-editor__editable_400"
              isInvalid={touched.instructor_notes && !!errors.instructor_notes}
            />
            <Form.Text>
              Instructor notes automatically propagate onto the Roster.pdf for Website Payment
              sessions.
            </Form.Text>
            <ErrorMessage
              name="instructor_notes"
              component="span"
              className="text-danger fs-small"
            />
          </Form.Group>
        </Stack>
      </Col>
    </Row>
  );
};
